import { SYSTEMS } from "@/utils/constants";
import { downloadFile, filterObject } from "@/utils/helpers";
import { useAxios } from "@/utils/hooks/useAxios";
import { useSystem } from "@/utils/hooks/useLocation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { toast } from "react-toastify";



// ============================================
// Queries
// ============================================

export const useGetMadinaManifestsQuery = (params = {}, enabled = true) => {
    const { axiosProtected } = useAxios();
    const [searchParams] = useSearchParams();
    const date = searchParams.get("date") || "today";
    const filteredParams = filterObject(params);
    const { currentSystem } = useSystem();

    const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem

    const URL = `${system}/reception/manifests` + (date === "all" ? "" : "/today");
    return useQuery({
        queryKey: ["madina-manifests", filteredParams, system, date],
        queryFn: () => axiosProtected.get(URL, {
            params: filteredParams,
        }),
        enabled,
    });
};

export const useGetMadinaManifestsBetweenCitiesQuery = (params = {}, enabled = true) => {
    const [searchParams] = useSearchParams();
    const date = searchParams.get("date") || "today";
    const { axiosProtected } = useAxios();
    const filteredParams = filterObject(params);
    const { currentSystem } = useSystem();
    const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem

    const URL = `${system}/reception/tafweejs` + (date === "all" ? "" : "/today");
    return useQuery({
        queryKey: ["madina-tafweejs", filteredParams, system, date],
        queryFn: () => axiosProtected.get(URL, {
            params: filteredParams,
        }),
        enabled,
    });
};
export const useGetMadinaManifestPilgrimsQuery = (params = {}, enabled = false, manifest_id = null) => {
    const { axiosProtected } = useAxios();
    const { currentSystem } = useSystem();
    const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem

    const filteredParams = filterObject(params);

    return useQuery({
        queryKey: ["madina-manifest-pilgrims", manifest_id, filteredParams],
        queryFn: () => axiosProtected.get(`${system}/reception/manifests/${manifest_id}/pilgrims`, {
            params: filteredParams,
        }),
        enabled,
    });
};
export const useGetMadinaTafweejPilgrimsQuery = (params = {}, enabled = false, manifest_id = null) => {
    const { axiosProtected } = useAxios();
    const { currentSystem } = useSystem();
    const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem

    const filteredParams = filterObject(params);

    return useQuery({
        queryKey: ["madina-tafweej-pilgrims", manifest_id, filteredParams],
        queryFn: () => axiosProtected.get(`${system}/reception/tafweejs/${manifest_id}/pilgrims`, {
            params: filteredParams,
        }),
        enabled,
    });
};


// ============================================
// Mutations
// ============================================

export const useCompleteMadinaManifestMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }) => axiosProtected.patch(`madina/reception/manifests/${id}/arrive-time`),
        onSuccess: () => {
            toast.success(t("COMPLETE_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-manifests"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};
export const useCancelMadinaManifestMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }) => axiosProtected.patch(`madina/reception/manifests/${id}/cancel-arrival`),
        onSuccess: () => {
            toast.success(t("CANCEL_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-manifests"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};

export const useAccommodateMadinaManifestPilgrimsMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();


    return useMutation({
        mutationFn: ({manifest_id, ...payload}) => axiosProtected.post(`madina/reception/manifests/${manifest_id}/bag-details/accommodate`, payload),
        onSuccess: () => {
            toast.success(t("ACCOMMODATE_PILGRIMS_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-manifests"] });
            queryClient.invalidateQueries({ queryKey: ["madina-manifest-pilgrims"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};
export const useCancelAccommodateMadinaManifestPilgrimsMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({manifest_id, ...payload}) => axiosProtected.post(`madina/reception/manifests/${manifest_id}/bag-details/cancel-accommodation`, payload),
        onSuccess: () => {
            toast.success(t("CANCEL_ACCOMMODATE_PILGRIMS_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-manifests"] });
            queryClient.invalidateQueries({ queryKey: ["madina-manifest-pilgrims"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};
export const useAccommodateMadinaTafweejPilgrimsMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({manifest_id, ...payload}) => axiosProtected.post(`madina/reception/tafweejs/${manifest_id}/bag-details/accommodate`, payload),
        onSuccess: () => {
            toast.success(t("ACCOMMODATE_PILGRIMS_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-tafweejs"] });
            queryClient.invalidateQueries({ queryKey: ["madina-tafweej-pilgrims"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};
export const useCancelAccommodateMadinaTafweejPilgrimsMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({manifest_id, ...payload}) => axiosProtected.post(`madina/reception/tafweejs/${manifest_id}/bag-details/cancel-accommodation`, payload),
        onSuccess: () => {
            toast.success(t("CANCEL_ACCOMMODATE_PILGRIMS_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-tafweejs"] });
            queryClient.invalidateQueries({ queryKey: ["madina-tafweej-pilgrims"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};

export const useAssignBagNumberToMadinaManifestMutation = ({ manifestId }) => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload) => axiosProtected.patch(`madina/reception/manifests/${manifestId}/bag-number`, payload),
        onSuccess: () => {
            toast.success(t("ASSIGN_BAG_NUMBER_TO_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-manifests"] });
        },
    });
};
export const useAssignBagNumberToMadinaTafweejMutation = ({ manifestId }) => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload) => axiosProtected.patch(`madina/reception/tafweejs/${manifestId}/bag-number`, payload),
        onSuccess: () => {
            toast.success(t("ASSIGN_BAG_NUMBER_TO_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-manifests"] });
        },
    });
};

export const useUnassignMadinaTafweejMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => axiosProtected.patch(`madina/reception/tafweejs/${id}/bag-number`, payload),
        onSuccess: () => {
            toast.success(t("CANCEL_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-tafweejs"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};

export const useCompleteMadinaTafweejMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }) => axiosProtected.patch(`madina/reception/tafweejs/${id}/arrive-time`),
        onSuccess: () => {
            toast.success(t("COMPLETE_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-tafweejs"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};

export const useCancelMadinaTafweejMutation = () => {
    const { axiosProtected } = useAxios();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }) => axiosProtected.patch(`madina/reception/tafweejs/${id}/cancel-arrival`),
        onSuccess: () => {
            toast.success(t("CANCEL_MANIFEST_SUCCESSFULLY"));
            queryClient.invalidateQueries({ queryKey: ["madina-tafweejs"] });
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};
export const useDownloadMadinaTafweejManifestMutation = () => {
    const { axiosProtected } = useAxios();
    const { currentSystem } = useSystem();
    const system = currentSystem === SYSTEMS.madina ? "madina" : currentSystem
    return useMutation({
        mutationFn: ({ id }) => axiosProtected.get(`${system}/nusk-arrival-flights/bags/by_number/${id}/print`, {
            responseType: "blob",
        }),
        onSuccess: (data, variables) => {
            const blob = new Blob([data.data], { type: "application/pdf" });
            downloadFile(blob, `حقيبه-${variables.id}.pdf`);
        },
        onError: (error) => {
            toast.error(error.response.data.message);
        },
    });
};